import { useState, useEffect, useCallback, useRef } from 'react';
import { playlistService } from '../services/playlistService';

const MIN_TEASER = 6;
const MAX_CONCURRENT_SEARCHES = 3;

function normName(name) {
  return (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

const COMPILATION_WORDS = /\b(hits|best|number|ones|greatest|top|classics|essential|masters|hi-res|hours?\s+of|rock|pop|soul|funk|disco|jazz|blues|r&b|rnb|wave|grunge|alternative|indie|metal|punk|britpop|motown|soundtrack|style|party|summer|club|forgotten|invasion|progressive|test|speaker|compilation|collection|mix|anthems|era|decade|60s|70s|80s|90s|19[6-9]\d|playlist|audiophile|demo|reference|hifi|hi-fi|sound|records|label|picks|selection|lounge|chill|focus|drive|morning|evening|night|fireside|acoustic|vocal|opera|classical|chamber|orchestra|symphony)\b/i;

function looksLikeCompilation(name) {
  if (!name) return false;
  if (COMPILATION_WORDS.test(name)) return true;
  const words = name.trim().split(/\s+/);
  return words.length >= 5;
}

// --- Module-level caches shared across all hook instances ---
let _hubConfigCache = null;
let _hubConfigPromise = null;
let _teaserExtrasCache = null;
let _teaserFillPromise = null;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
let _hubConfigCacheTime = 0;

export function fetchHubConfigCached() {
  if (_hubConfigCache && Date.now() - _hubConfigCacheTime < CACHE_TTL_MS) {
    return Promise.resolve(_hubConfigCache);
  }
  if (_hubConfigPromise) return _hubConfigPromise;
  _hubConfigPromise = playlistService.getHubConfig().then(hub => {
    _hubConfigCache = hub;
    _hubConfigCacheTime = Date.now();
    _hubConfigPromise = null;
    return hub;
  }).catch(err => {
    _hubConfigPromise = null;
    throw err;
  });
  return _hubConfigPromise;
}

async function searchThrottled(queries, limit) {
  const results = [];
  for (let i = 0; i < queries.length; i += MAX_CONCURRENT_SEARCHES) {
    const batch = queries.slice(i, i + MAX_CONCURRENT_SEARCHES);
    const batchResults = await Promise.all(
      batch.map(q => playlistService.search(q, limit).catch(() => ({ playlists: [] })))
    );
    results.push(...batchResults);
  }
  return results;
}

function fillTeasersCached(hubConfig) {
  if (_teaserExtrasCache) return Promise.resolve(_teaserExtrasCache);
  if (_teaserFillPromise) return _teaserFillPromise;

  const sparse = (hubConfig?.sections || []).filter(s =>
    (s.group === 'genre' || s.group === 'mood' || s.group === 'season') && (s.playlists || []).length < MIN_TEASER
  );
  if (sparse.length === 0) {
    _teaserExtrasCache = {};
    return Promise.resolve(_teaserExtrasCache);
  }

  const allQueries = [];
  const queryMap = [];
  for (const section of sparse) {
    const hiddenSet = new Set(section.hidden || []);
    const pinned = (section.playlists || []).filter(p => !hiddenSet.has(p.servicePlaylistId || p.id));
    const queries = section.searchQueries || [section.label];
    for (const q of queries) {
      allQueries.push(q);
      queryMap.push({ sectionId: section.id, pinned, hiddenSet, needed: MIN_TEASER - pinned.length });
    }
  }

  _teaserFillPromise = searchThrottled(allQueries, 20).then(results => {
    const sectionExtras = {};
    const sectionSeen = {};
    for (let i = 0; i < results.length; i++) {
      const { sectionId, pinned, hiddenSet, needed } = queryMap[i];
      if (!sectionExtras[sectionId]) {
        sectionExtras[sectionId] = [];
        const ids = new Set([...pinned.map(p => p.servicePlaylistId || p.id), ...hiddenSet]);
        const names = new Set(pinned.map(p => normName(p.name)));
        sectionSeen[sectionId] = { ids, names };
      }
      const extra = sectionExtras[sectionId];
      const seen = sectionSeen[sectionId];
      if (extra.length >= needed) continue;
      for (const p of results[i].playlists || []) {
        if (extra.length >= needed) break;
        const pid = p.servicePlaylistId || p.id;
        const nn = normName(p.name);
        if (seen.ids.has(pid) || seen.names.has(nn)) continue;
        if (!looksLikeCompilation(p.name)) continue;
        seen.ids.add(pid);
        seen.names.add(nn);
        extra.push(p);
      }
    }
    const map = {};
    for (const [id, extra] of Object.entries(sectionExtras)) {
      if (extra.length > 0) map[id] = extra;
    }
    _teaserExtrasCache = map;
    _teaserFillPromise = null;
    return map;
  }).catch(() => {
    _teaserFillPromise = null;
    return {};
  });
  return _teaserFillPromise;
}

export default function usePlaylistHub() {
  const [hubConfig, setHubConfig] = useState(_hubConfigCache);
  const [loading, setLoading] = useState(!_hubConfigCache);
  const [error, setError] = useState(null);
  const [teaserExtras, setTeaserExtras] = useState(_teaserExtrasCache || {});

  const [curateMode, setCurateMode] = useState(false);
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem('maestro_admin_key') || '');
  const [showAdminPrompt, setShowAdminPrompt] = useState(false);
  const [adminKeyInput, setAdminKeyInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [dirty, setDirty] = useState(false);

  const [addTarget, setAddTarget] = useState(null);
  const [addUrl, setAddUrl] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (_hubConfigCache && Date.now() - _hubConfigCacheTime < CACHE_TTL_MS) {
      setHubConfig(_hubConfigCache);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchHubConfigCached()
      .then(hub => { if (!cancelled) { setHubConfig(hub); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!hubConfig?.sections) return;
    let cancelled = false;
    fillTeasersCached(hubConfig).then(map => {
      if (!cancelled) setTeaserExtras(map);
    });
    return () => { cancelled = true; };
  }, [hubConfig]);

  const handleEnterCurate = useCallback(() => {
    if (adminKey) setCurateMode(true);
    else setShowAdminPrompt(true);
  }, [adminKey]);

  const handleAdminKeySubmit = useCallback((e) => {
    e.preventDefault();
    if (adminKeyInput.trim()) {
      const key = adminKeyInput.trim();
      setAdminKey(key);
      sessionStorage.setItem('maestro_admin_key', key);
      setCurateMode(true);
      setShowAdminPrompt(false);
      setAdminKeyInput('');
    }
  }, [adminKeyInput]);

  const handleExitCurate = useCallback(() => {
    setCurateMode(false);
    setDirty(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!hubConfig) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await playlistService.saveHubConfig({ sections: hubConfig.sections }, adminKey);
      setSaveMessage('Saved');
      setDirty(false);
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      if (err.message?.includes('401')) {
        setSaveMessage('Invalid admin key');
        sessionStorage.removeItem('maestro_admin_key');
        setAdminKey('');
      } else {
        setSaveMessage(`Error: ${err.message}`);
      }
    }
    setSaving(false);
  }, [hubConfig, adminKey]);

  const handleRemovePlaylist = useCallback((sectionId, playlist) => {
    const pid = playlist.servicePlaylistId || playlist.id;
    setHubConfig(prev => {
      const next = { ...prev, sections: prev.sections.map(s => {
        if (s.id !== sectionId || s.type === 'user') return s;
        const isPinned = (s.playlists || []).some(p =>
          (p.servicePlaylistId || p.id) === pid
        );
        const hidden = [...(s.hidden || [])];
        if (!hidden.includes(String(pid))) hidden.push(String(pid));
        return {
          ...s,
          playlists: isPinned
            ? (s.playlists || []).filter(p => (p.servicePlaylistId || p.id) !== pid)
            : s.playlists,
          hidden,
        };
      })};
      return next;
    });
    // Clear expand cache so the removed playlist disappears immediately
    if (expandCache.current[sectionId]) {
      expandCache.current[sectionId] = expandCache.current[sectionId].filter(
        p => (p.servicePlaylistId || p.id) !== pid
      );
      setExpandedPlaylists(prev => prev.filter(
        p => (p.servicePlaylistId || p.id) !== pid
      ));
    }
    setDirty(true);
  }, []);

  const handleAddPlaylist = useCallback(async (e) => {
    e.preventDefault();
    if (!addUrl.trim() || !addTarget) return;
    setAddLoading(true);
    setAddError(null);
    try {
      const match = addUrl.match(/playlists?\/(\d+)/i) || addUrl.match(/(\d{5,})/);
      if (!match) throw new Error('Could not extract playlist ID from URL');
      const detail = await playlistService.getDetail(match[1]);
      const entry = {
        servicePlaylistId: detail.servicePlaylistId || String(match[1]),
        name: detail.name || 'Unknown',
        imageUrl: detail.imageUrl || null,
        trackCount: detail.trackCount || 0,
        owner: detail.owner || '',
      };
      setHubConfig(prev => {
        const next = { ...prev, sections: prev.sections.map(s => {
          if (s.id !== addTarget) return s;
          const exists = (s.playlists || []).some(p => p.servicePlaylistId === entry.servicePlaylistId);
          if (exists) return s;
          return { ...s, playlists: [...(s.playlists || []), entry] };
        })};
        return next;
      });
      setDirty(true);
      setAddUrl('');
      setAddTarget(null);
    } catch (err) {
      setAddError(err.message || 'Could not load playlist');
    }
    setAddLoading(false);
  }, [addUrl, addTarget]);

  const sections = hubConfig?.sections || [];

  // Expanded section: fetch live Qobuz results to fill out the grid (~100 playlists)
  const [expandedId, setExpandedId] = useState(null);
  const [expandedPlaylists, setExpandedPlaylists] = useState([]);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const expandCache = useRef({});

  const expandSection = useCallback(async (sectionId) => {
    setExpandedId(sectionId);
    window.scrollTo({ top: 0 });

    if (expandCache.current[sectionId]) {
      setExpandedPlaylists(expandCache.current[sectionId]);
      return;
    }

    const section = (hubConfig?.sections || []).find(s => s.id === sectionId);
    if (!section) return;

    setExpandedLoading(true);
    const hiddenSet = new Set(section.hidden || []);
    const pinned = (section.playlists || []).filter(p => !hiddenSet.has(p.servicePlaylistId || p.id));
    const queries = section.searchQueries || [section.label];
    const seenIds = new Set([...pinned.map(p => p.servicePlaylistId || p.id), ...hiddenSet]);
    const seenNames = new Set(pinned.map(p => normName(p.name)));
    let extra = [];

    try {
      const results = await searchThrottled(queries, 50);
      for (const r of results) {
        for (const p of r.playlists || []) {
          const pid = p.servicePlaylistId || p.id;
          const nn = normName(p.name);
          if (seenIds.has(pid) || seenNames.has(nn)) continue;
          if (!looksLikeCompilation(p.name)) continue;
          seenIds.add(pid);
          seenNames.add(nn);
          extra.push(p);
        }
      }
    } catch {}

    const merged = [...pinned, ...extra];
    expandCache.current[sectionId] = merged;
    setExpandedPlaylists(merged);
    setExpandedLoading(false);
  }, [hubConfig]);

  const collapseSection = useCallback(() => {
    setExpandedId(null);
    setExpandedPlaylists([]);
  }, []);

  const getTeaserPlaylists = useCallback((section) => {
    const hiddenSet = new Set(section.hidden || []);
    const base = (section.playlists || []).filter(p => !hiddenSet.has(p.servicePlaylistId || p.id));
    const extras = (teaserExtras[section.id] || []).filter(p => !hiddenSet.has(p.servicePlaylistId || p.id));
    const all = [...base, ...extras];
    // Push "audiophile"-labeled playlists to the end so teasers lead with variety
    return all.sort((a, b) => {
      const aHas = /audiophile/i.test(a.name || '');
      const bHas = /audiophile/i.test(b.name || '');
      if (aHas && !bHas) return 1;
      if (!aHas && bHas) return -1;
      return 0;
    });
  }, [teaserExtras]);

  return {
    hubConfig, loading, error, sections,
    curateMode, dirty, saving, saveMessage,
    showAdminPrompt, adminKeyInput, setAdminKeyInput,
    addTarget, setAddTarget, addUrl, setAddUrl, addLoading, addError, setAddError,
    handleEnterCurate, handleAdminKeySubmit, handleExitCurate, handleSave,
    handleRemovePlaylist, handleAddPlaylist,
    expandedId, expandedPlaylists, expandedLoading, expandSection, collapseSection,
    getTeaserPlaylists,
  };
}
